import { gql } from '@apollo/client';
import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import urljoin from 'url-join';
import { LeaderboardFundraisersListQuery } from '../../../types/__generated__';
import config from '../../client/config';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import BenefactorListItem from '../BenefactorListItem/BenefactorListItem';
import BenefactorListItemSkeleton from '../BenefactorListItem/BenefactorListItemSkeleton';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import PageSection from '../PageSection/PageSection';
import styles from './FundraisersList.scss';

const DOCUMENT = gql`
  query LeaderboardFundraisersList(
    $after: String
    $type: PageType!
    $slug: Slug!
  ) {
    page(type: $type, slug: $slug) {
      id
      fundraisers: leaderboard(type: FUNDRAISERS, first: 5, after: $after) {
        edges {
          node {
            id
            slug
            owner {
              id
              avatar
              name
            }
            donations(first: 1) {
              totalCount
            }
            donationSummary {
              totalAmount {
                value
                currencyCode
              }
            }
            targetWithCurrency {
              value
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const LeaderboardFundraiserList: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { loading, error, data, fetchMore } = useFullPageQuery<
    LeaderboardFundraisersListQuery
  >(DOCUMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (loading || error) {
    return (
      <PageSection title={t('fundraisers.title', 'Fundraisers')} hasBorder>
        <BenefactorListItemSkeleton animated={false} />
      </PageSection>
    );
  }

  const fundraisers = data?.page?.fundraisers;
  // TODO: this needs to come from either relationships or totals
  const totalCount: number = fundraisers?.edges.length === 0 ? 0 : -1;

  if (!fundraisers) {
    return null;
  }

  async function handleFetchMore() {
    await fetchMore({
      variables: {
        after: fundraisers!.pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          !previousResult?.page?.fundraisers ||
          !fetchMoreResult?.page?.fundraisers
        ) {
          return previousResult;
        }

        return {
          page: {
            ...previousResult.page,
            fundraisers: {
              ...fetchMoreResult.page.fundraisers,
              edges: [
                ...previousResult.page.fundraisers.edges,
                ...fetchMoreResult.page.fundraisers.edges,
              ],
            },
          },
        };
      },
    });
  }

  return (
    <PageSection
      title={
        totalCount === 0
          ? t('fundraisers.noFundraisers', 'No fundraisers yet')
          : t('fundraisers.title', 'Fundraisers')
      }
      hasBorder
      count={totalCount}
      className={styles.container}
    >
      <ul data-qa="fundraisers-list">
        {fundraisers.edges.map(fundraiser => {
          const {
            id,
            owner,
            donations,
            donationSummary,
            slug,
            targetWithCurrency,
          } = fundraiser.node;

          let totaliserValue;
          if (targetWithCurrency) {
            totaliserValue = Math.round(
              (donationSummary?.totalAmount?.value / targetWithCurrency.value) *
                100,
            );
          }

          return (
            <BenefactorListItem
              key={id}
              title={owner?.name}
              image={
                owner?.avatar ? `${owner?.avatar}?template=${SIZE_136_136}` : ''
              }
              currencyCode={donationSummary?.totalAmount?.currencyCode || 'GBP'}
              amount={
                donationSummary?.totalAmount?.value
                  ? donationSummary.totalAmount.value / 100
                  : 0
              }
              totaliserValue={totaliserValue}
              supporterCount={donations?.totalCount || 0}
              url={urljoin(config.baseUri, slug)}
            />
          );
        })}
      </ul>
      {fundraisers.pageInfo.hasNextPage && (
        <LoadMoreButton
          className="jg-btn"
          loading={loading}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'campaign members',
          }}
        >
          <Trans i18nKey="fundraisers.showMore">
            Show more
            <span className="jg-sr-only"> fundraisers</span>
          </Trans>
        </LoadMoreButton>
      )}
    </PageSection>
  );
};

export default LeaderboardFundraiserList;
